/**
 * Date/time utilities wrapping date-fns + date-fns-tz.
 *
 * Key principle: always be explicit about timezones.
 * - Use parseDate() for date-only values (no timezone ambiguity)
 * - Use parseDateTime() for datetime values in a specific timezone
 * - Use toUTC() to convert to UTC for storage
 * - Use formatInTz() for display in a user's timezone
 *
 * IMPORTANT: Never use `new Date(dateString)` directly — it parses as UTC
 * which causes off-by-one-day bugs in timezones behind UTC.
 */
import { format, parseISO, isValid, startOfDay, endOfDay, addDays, addHours, addMinutes, differenceInMinutes, differenceInHours, differenceInDays, isBefore, isAfter, isEqual } from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
// ─── Parsing ──────────────────────────────────────────────
/**
 * Parse a date-only string (YYYY-MM-DD) as the start of that day in the given timezone.
 * Prevents the UTC midnight bug where "2026-03-15" becomes March 14 in EST.
 */
export function parseDate(dateStr, timezone) {
    // Parse as local date in the given timezone
    const [year, month, day] = dateStr.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);
    return fromZonedTime(localDate, timezone);
}
/**
 * Parse a (YYYY-MM-DD, HH:MM[:SS]) pair as a wall-clock time in the given timezone
 * and return the corresponding UTC instant. Server-TZ-independent.
 *
 * Example:
 *   parseDateTimeInTz("2026-05-26", "08:00", "America/New_York")
 *   // → 2026-05-26T12:00:00.000Z   (8am EDT)
 *
 * Implementation detail: passes a naive ISO string (no offset) to date-fns-tz's
 * `fromZonedTime`, which interprets the wall-clock components as being in the
 * given timezone. This is server-TZ-safe; the Date-object overload of
 * `fromZonedTime` reads system-local components and would drift on a non-UTC
 * server.
 */
export function parseDateTimeInTz(dateStr, timeStr, timezone) {
    const withSeconds = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    return fromZonedTime(`${dateStr}T${withSeconds}`, timezone);
}
/**
 * Re-interpret a naive `timestamp without time zone` value as a real instant in
 * the given timezone.
 *
 * Columns like `visits.start_time` store a local wall-clock with NO offset; the
 * row's `timezone` column says which zone that wall-clock belongs to. postgres-js
 * reads such a column back as a Date whose UTC components ARE the stored
 * wall-clock (e.g. DB "2026-06-23 10:00:00" → Date 2026-06-23T10:00:00.000Z), so
 * comparing it directly to a real `new Date()` on a UTC server treats 10am New
 * York as 10:00 UTC — four hours early. This reads those UTC components back out
 * as a wall-clock string and re-anchors them in the target timezone.
 *
 * Example:
 *   naiveLocalToInstant(new Date("2026-06-23T10:00:00.000Z"), "America/New_York")
 *   // → 2026-06-23T14:00:00.000Z   (10am EDT)
 *
 * Server-TZ-independent: formats in UTC, never reads system-local components.
 */
export function naiveLocalToInstant(naive, timezone) {
    const wallClock = formatInTimeZone(naive, "UTC", "yyyy-MM-dd'T'HH:mm:ss");
    return fromZonedTime(wallClock, timezone);
}
/**
 * Parse an ISO datetime string. If no timezone offset in the string,
 * treats it as UTC.
 */
export function parseDateTime(isoStr) {
    const date = parseISO(isoStr);
    if (!isValid(date)) {
        throw new Error(`Invalid datetime: ${isoStr}`);
    }
    return date;
}
// ─── Formatting ───────────────────────────────────────────
/**
 * Format a Date in a specific timezone.
 * Use for display to users — always show times in their local timezone.
 *
 * Example:
 *   formatInTz(visit.clockInAt, "America/New_York", "yyyy-MM-dd HH:mm")
 *   // → "2026-03-15 14:30"
 */
export function formatInTz(date, timezone, fmt) {
    return formatInTimeZone(date, timezone, fmt);
}
/**
 * Format a Date as ISO date string (YYYY-MM-DD) in the given timezone.
 */
export function formatDateOnly(date, timezone) {
    return formatInTimeZone(date, timezone, "yyyy-MM-dd");
}
/**
 * Format a Date as ISO datetime string in UTC.
 * Use for API responses and database storage.
 */
export function formatUTC(date) {
    return date.toISOString();
}
/**
 * Format a Date using date-fns format (UTC-based).
 */
export function formatDate(date, fmt) {
    return format(date, fmt);
}
// ─── LLM date reference ───────────────────────────────────
/**
 * Build a human-readable date reference table for LLM prompts.
 *
 * LLMs (and speech-native voice models even more so) are reliable at LOOKING UP
 * dates but unreliable at COMPUTING them — "what weekday is the 25th", "is the
 * 25th tomorrow". Handing the model a pre-computed table of nearby dates, each
 * with its weekday, ISO form, and a relative label (TODAY / YESTERDAY /
 * TOMORROW / "in N days"), removes the arithmetic and the recurring relative-date
 * mistakes seen across the manager bot, chat bot, and voice agents.
 *
 * The table is anchored to the wall-clock calendar date in `timezone` (NOT the
 * server's UTC date), so "today" matches the user's local day even late at
 * night. Each surface passes the timezone that matters for it (the patient's
 * timezone for the manager bot, America/New_York for the chat + voice agents).
 *
 * Each row reads: `June 25, 2026, Thursday (2026-06-25) — TODAY`
 * The long date is for speaking/writing to the caller; the (YYYY-MM-DD) value is
 * what the model should pass to any tool that takes a date.
 */
export function buildDateReferenceBlock(now, timezone, opts = {}) {
    const daysBack = opts.daysBack ?? 7;
    const daysForward = opts.daysForward ?? 14;
    // Anchor "today" to the local calendar date in `timezone`, not the server's.
    const [year, month, day] = formatInTimeZone(now, timezone, "yyyy-MM-dd").split("-").map(Number);
    const rows = [];
    for (let offset = -daysBack; offset <= daysForward; offset++) {
        // Date.UTC normalizes month/year rollover; formatting back in UTC keeps
        // the calendar date stable regardless of the server's own timezone.
        const date = new Date(Date.UTC(year, month - 1, day + offset));
        const longDate = formatInTimeZone(date, "UTC", "MMMM d, yyyy");
        const weekday = formatInTimeZone(date, "UTC", "EEEE");
        const iso = formatInTimeZone(date, "UTC", "yyyy-MM-dd");
        rows.push(`${longDate}, ${weekday} (${iso}) — ${relativeDayLabel(offset)}`);
    }
    const rightNow = formatInTimeZone(now, timezone, "EEEE, MMMM d yyyy, h:mm a zzz");
    return [
        `=== DATE REFERENCE (${timezone}) ===`,
        `Right now it is ${rightNow}.`,
        "",
        ...rows
    ].join("\n");
}
function relativeDayLabel(offset) {
    if (offset === 0) {
        return "TODAY";
    }
    if (offset === -1) {
        return "YESTERDAY";
    }
    if (offset === 1) {
        return "TOMORROW";
    }
    if (offset < 0) {
        return `${-offset} days ago`;
    }
    return `in ${offset} days`;
}
// ─── Timezone conversion ──────────────────────────────────
/**
 * Convert a zoned datetime to UTC.
 * Example: "2026-03-15 14:30 in New York" → UTC Date
 */
export function toUTC(date, timezone) {
    return fromZonedTime(date, timezone);
}
/**
 * Convert a UTC Date to a specific timezone.
 * Example: UTC Date → "what time is it in New York?"
 */
export function toTimezone(date, timezone) {
    return toZonedTime(date, timezone);
}
// ─── Day boundaries ───────────────────────────────────────
/**
 * Get start of day in a specific timezone, returned as UTC.
 */
export function startOfDayInTz(date, timezone) {
    const zoned = toZonedTime(date, timezone);
    const dayStart = startOfDay(zoned);
    return fromZonedTime(dayStart, timezone);
}
/**
 * Get end of day in a specific timezone, returned as UTC.
 */
export function endOfDayInTz(date, timezone) {
    const zoned = toZonedTime(date, timezone);
    const dayEnd = endOfDay(zoned);
    return fromZonedTime(dayEnd, timezone);
}
// ─── Re-exports for convenience ───────────────────────────
// Consumers import from @taskhealth/shared instead of date-fns directly
export { isValid, addDays, addHours, addMinutes, differenceInMinutes, differenceInHours, differenceInDays, isBefore, isAfter, isEqual };
//# sourceMappingURL=dates.js.map